import type { TypeOrArray } from '@beesoft/common';
import { cloneDeep } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown, DropDownSelectionType } from '../../../..';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '../../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../../../common/hooks/use-test-tag';
import type { ContinuousScrollerFilter } from '../../../continuous-scroller/continuous-scroller-types';
import { DataSearchLocation } from '../../../enums';
import type { ActionSectionItem } from '../../../fields/drop-down/drop-down-action-section.component';
import { isDropDownNoSelection } from '../../../fields/drop-down/drop-down-common';
import type { DropDownFeatures } from '../../../fields/drop-down/drop-down-features';
import type {
  DropDownNoSelection,
  DropDownResult,
  DropDownSelectorTemplateProps,
} from '../../../fields/drop-down/drop-down-types';
import type { MobileFilterInterface, MobileFilterType } from '../data-grid-filter.interface';

export interface MobileSingleDropDownFilter extends MobileFilterInterface {
  search?: OdinDataRetrieval;
  componentFilters?: MobileFilterType;
}

export default function MobileMultiSelectDropDownFilter({
  search,
  column,
  onInputChange,
  id,
  componentFilters,
}: MobileSingleDropDownFilter) {
  const { t } = useTranslation();
  const tagCreator = useTestTag(column.title || '');

  const filterValue = useMemo(() => {
    return (componentFilters?.[id]?.value ?? []) as unknown as Array<DropDownResult>;
  }, [componentFilters?.[id], id]);

  const blankFilter: DropDownResult = useMemo(
    () => ({
      value: 'blank',
      text: t('blank') ?? 'Blank',
    }),
    [t]
  );

  if (!column.dropDownText || !column.dropDownValue) {
    throw new Error('The dropDownText and dropDownValue properties must be set to use the drop down filter');
  }

  const dropDownData = useCallback(
    (scrollerSubscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (search && !Array.isArray(search)) {
        const scrollerOptions: OdinDataRetrievalOptions = {
          ...options,
          customProperties: { ...column.customDataProperties, ...options?.customProperties },
        };

        search.getData(scrollerSubscriber, scrollerOptions);
      }
    },
    [search, column.customDataProperties]
  );

  const dataRetriever: OdinDataRetrieval = useMemo(() => {
    if (search && !Array.isArray(search) && search.getSubscriber) {
      return {
        getData: dropDownData,
        getSubscriber: search.getSubscriber,
      };
    }

    return {
      getData: dropDownData,
    };
  }, [search, dropDownData]);

  const actionItems = useMemo<Array<ActionSectionItem> | undefined>(() => {
    const actionItems: Array<ActionSectionItem> = [];

    if (column?.showBlankAction === true) {
      const currentSearchValue = Array.isArray(filterValue) ? filterValue : [filterValue];
      const isBlankValueSelected = currentSearchValue.some((value) => value.value === blankFilter.value);
      actionItems.push({ ...blankFilter, selected: isBlankValueSelected });
    }

    if (column?.showArchivedAction === true) {
      actionItems.push({
        value: 'archived',
        text: t('show-archived-values') ?? 'Show Archived Values',
        selected: Boolean(column.includeArchived),
      });
    }

    return actionItems.length > 0 ? actionItems : undefined;
  }, [blankFilter, column?.showArchivedAction, column?.showBlankAction, column.includeArchived, filterValue, t]);

  const onSelectionChange = (value?: TypeOrArray<DropDownResult> | DropDownNoSelection) => {
    if (value && !isDropDownNoSelection(value) && Array.isArray(value) && value.length > 0) {
      const filterValues = getFilterValues(value);
      onInputChange({ column, key: id, value: filterValues, comparison: column.searchComparison });
    } else {
      const filterValues = getFilterValues([]);
      if (filterValues.length > 0) {
        onInputChange({ column, key: id, value: filterValues, comparison: column.searchComparison });
      } else {
        onInputChange({ column, key: id, value: undefined, comparison: column.searchComparison });
      }
    }
  };

  const getFilterValues = (value: Array<DropDownResult>) => {
    const selectedValues = cloneDeep(value);
    const isBlankFilterIncluded =
      Array.isArray(filterValue) && filterValue.some((filter) => filter.value === String(blankFilter.value));

    if (isBlankFilterIncluded && selectedValues.every((val) => val.value !== blankFilter.value)) {
      selectedValues.push(blankFilter);
    }

    return selectedValues;
  };

  const handleActionItemChanged = (item: ActionSectionItem, features: DropDownFeatures) => {
    if (item.value === 'archived') {
      const filter: ContinuousScrollerFilter = {
        scrollerId: column.id as string | number,
        field: String(item.value),
        value: Boolean(item.selected),
        comparison: 1,
      };

      features.addFilter(filter);

      column.includeArchived = Boolean(item.selected);
    } else if (item.value === blankFilter.value) {
      if (item.selected) {
        const currentFilter = Array.isArray(filterValue) ? filterValue : [filterValue];
        onInputChange({
          column,
          key: id,
          value: [...currentFilter, ...[blankFilter]],
          comparison: column.searchComparison,
        });
      } else {
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          const filterValueWithoutBlank = filterValue.filter((filter) => filter.value !== String(blankFilter.value));
          onInputChange({ column, key: id, value: filterValueWithoutBlank, comparison: column.searchComparison });
        } else {
          onInputChange({ column, key: id, value: [], comparison: column.searchComparison });
        }
      }
    }
  };

  const selectorTemplate = ({ selectedValue, multipleMessageCount }: DropDownSelectorTemplateProps) => {
    const selectedValues = cloneDeep(selectedValue ?? []);
    const isBlankFilterIncluded =
      Array.isArray(filterValue) && filterValue.some((filter) => filter.value === String(blankFilter.value));

    if (isBlankFilterIncluded && selectedValues.every((val) => val.value !== blankFilter.value)) {
      selectedValues.push(blankFilter);
    }

    if (selectedValues.length >= multipleMessageCount) {
      return <>{t('multiple', 'Multiple')}</>;
    }

    return <>{selectedValues.map((val) => val.text).join(', ')}</>;
  };

  return (
    <div className="odin-w-full" ref={tagCreator('drop-down-filter')}>
      <DropDown
        id={column.id}
        textField={column.dropDownText}
        valueField={column.dropDownValue}
        archivedField={column.dropDownArchived}
        value={filterValue}
        selectionType={DropDownSelectionType.Multiple}
        data={column.dropDownData ?? dataRetriever}
        multipleMessageCount={3}
        useChips={false}
        allowClear
        allowSearch={true}
        showArchivedOptions={column.includeArchived}
        onChange={onSelectionChange}
        filterLocation={DataSearchLocation.Api}
        shouldUseCreatePortal={false}
        actionItems={actionItems}
        onActionItemChanged={handleActionItemChanged}
        selectorTemplate={selectorTemplate}
      />
    </div>
  );
}
